<template>
  <section class="workspace-stack">
    <div class="workspace-grid workspace-grid--split">
      <article class="glass-surface workspace-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Codigos del evento</span>
            <h2 class="workspace-title">Controla codigos y links privados</h2>
            <p class="workspace-copy">Crea codigos manuales, genera lotes y comparte links directos para tus campañas.</p>
          </div>
          <div class="workspace-badge-row">
            <span class="workspace-badge workspace-badge--mint"><i class="fas fa-key"></i> {{ activeCodes.length }} activos</span>
            <span class="workspace-badge"><i class="fas fa-archive"></i> {{ archivedCodes.length }} archivados</span>
          </div>
        </div>

        <div class="workspace-metrics">
          <article class="workspace-metric">
            <small>Total codigos</small>
            <strong>{{ codes.length }}</strong>
          </article>
          <article class="workspace-metric">
            <small>Activos</small>
            <strong>{{ activeCodes.length }}</strong>
          </article>
          <article class="workspace-metric">
            <small>Archivados</small>
            <strong>{{ archivedCodes.length }}</strong>
          </article>
        </div>
      </article>

      <article class="glass-surface workspace-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Manual</span>
            <h2 class="workspace-title">Codigo manual</h2>
          </div>
        </div>
        <form class="workspace-form" @submit.prevent="createCode">
          <label class="workspace-field">
            <span>Codigo</span>
            <input v-model="form.code" class="workspace-input" type="text" placeholder="VIP-LAUNCH" />
          </label>
          <label class="workspace-field">
            <span>Etiqueta</span>
            <input v-model="form.label" class="workspace-input" type="text" placeholder="Influencers / Staff / Early access" />
          </label>
          <label class="workspace-inline-check">
            <input v-model="form.is_active" type="checkbox" />
            <span>Crear activo</span>
          </label>
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="loading">
            <i class="fas fa-plus-circle"></i>
            <span>Crear codigo</span>
          </button>
        </form>
      </article>
    </div>

    <div class="workspace-grid workspace-grid--split">
      <article class="glass-surface workspace-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Lote</span>
            <h2 class="workspace-title">Generacion automatica</h2>
          </div>
        </div>
        <form class="workspace-form workspace-form--three" @submit.prevent="generateCodes">
          <label class="workspace-field">
            <span>Cantidad</span>
            <input v-model.number="bulkForm.quantity" class="workspace-input" type="number" min="1" max="500" />
          </label>
          <label class="workspace-field">
            <span>Prefijo</span>
            <input v-model="bulkForm.prefix" class="workspace-input" type="text" placeholder="VIP" />
          </label>
          <label class="workspace-field">
            <span>Etiqueta base</span>
            <input v-model="bulkForm.label_prefix" class="workspace-input" type="text" placeholder="Campana influencers" />
          </label>
          <button class="workspace-button workspace-button--secondary" type="submit" :disabled="loading">
            <i class="fas fa-bolt"></i>
            <span>Generar lote</span>
          </button>
        </form>
      </article>

      <article class="glass-surface workspace-panel">
        <div class="workspace-panel__header">
          <div>
            <span class="workspace-kicker">Importacion</span>
            <h2 class="workspace-title">Importar CSV</h2>
          </div>
        </div>
        <form class="workspace-form" @submit.prevent="importCodes">
          <label class="workspace-field">
            <span>Archivo CSV</span>
            <input class="workspace-input" type="file" accept=".csv" @change="onFileSelected" />
          </label>
          <button class="workspace-button" type="submit" :disabled="loading || !importFile">
            <i class="fas fa-file-import"></i>
            <span>Importar codigos</span>
          </button>
        </form>
      </article>
    </div>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Biblioteca</span>
          <h2 class="workspace-title">Todos los codigos</h2>
        </div>
      </div>

      <div v-if="loading && !codes.length" class="workspace-empty">Cargando codigos...</div>
      <div v-else-if="!codes.length" class="workspace-empty">Todavia no tienes codigos. Crea uno manual o genera un lote para empezar.</div>
      <div v-else class="workspace-code-grid">
        <article v-for="eventCode in codes" :key="eventCode.id" class="workspace-code-card">
          <div class="workspace-code-card__top">
            <div>
              <span class="workspace-code-card__eyebrow">{{ sourceLabel(eventCode.source) }}</span>
              <h3>{{ eventCode.code }}</h3>
              <p>{{ eventCode.label || 'Sin etiqueta' }}</p>
            </div>
            <span :class="['workspace-status-pill', eventCode.is_active ? 'is-active' : 'is-off']">{{ eventCode.is_active ? 'Activo' : 'Archivado' }}</span>
          </div>

          <div class="workspace-code-card__meta">
            <span><i class="fas fa-calendar-alt"></i> {{ formatDate(eventCode.created_at) }}</span>
            <span><i class="fas fa-link"></i> {{ promoUrl(eventCode.code) }}</span>
          </div>

          <div class="workspace-code-card__actions">
            <button class="workspace-chip-button" type="button" @click="copyPromoLink(eventCode.code)">
              <i class="fas fa-copy"></i>
              <span>Copiar link</span>
            </button>
            <button class="workspace-chip-button" type="button" @click="toggleStatus(eventCode)">
              <i :class="eventCode.is_active ? 'fas fa-eye-slash' : 'fas fa-rotate-left'"></i>
              <span>{{ eventCode.is_active ? 'Archivar' : 'Reactivar' }}</span>
            </button>
          </div>
        </article>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'

export default {
  name: 'CodesView',

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      codes: [],
      importFile: null,
      errorMessage: '',
      successMessage: '',
      form: {
        code: '',
        label: '',
        is_active: true,
      },
      bulkForm: {
        quantity: 10,
        prefix: '',
        label_prefix: '',
      },
    }
  },

  computed: {
    activeCodes() {
      return this.codes.filter((item) => item.is_active)
    },

    archivedCodes() {
      return this.codes.filter((item) => !item.is_active)
    },
  },

  created() {
    this.fetchCodes()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.fetchCodes()
      },
    },
  },

  methods: {
    fetchCodes() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loading = true
      apiService(`/api/host/events/${this.event.id}/codes/`).then((data) => {
        this.codes = Array.isArray(data) ? data : []
      }).finally(() => {
        this.loading = false
      })
    },

    sourceLabel(source) {
      if (Number(source) === 2) {
        return 'Lote'
      }
      if (Number(source) === 3) {
        return 'Importado'
      }
      return 'Manual'
    },

    promoUrl(code) {
      return `/event/${this.event.slug}?co=${encodeURIComponent(code)}&wanted=true`
    },

    formatDate(value) {
      if (!value) {
        return 'Ahora'
      }

      return new Intl.DateTimeFormat('default', { dateStyle: 'medium' }).format(new Date(value))
    },

    resetMessages() {
      this.errorMessage = ''
      this.successMessage = ''
    },

    createCode() {
      this.resetMessages()
      this.loading = true
      apiService(`/api/host/events/${this.event.id}/codes/`, 'POST', this.form).then((data) => {
        if (data && !data.err) {
          this.successMessage = `Codigo ${data.code} creado.`
          this.form = { code: '', label: '', is_active: true }
          this.fetchCodes()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo crear el codigo.'
      }).finally(() => {
        this.loading = false
      })
    },

    generateCodes() {
      this.resetMessages()
      this.loading = true
      apiService(`/api/host/events/${this.event.id}/codes/bulk/`, 'POST', this.bulkForm).then((data) => {
        if (Array.isArray(data)) {
          this.successMessage = `Se generaron ${data.length} codigos.`
          this.fetchCodes()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo generar el lote.'
      }).finally(() => {
        this.loading = false
      })
    },

    onFileSelected(event) {
      this.importFile = event.target.files && event.target.files[0] ? event.target.files[0] : null
    },

    importCodes() {
      if (!this.importFile) {
        return
      }

      this.resetMessages()
      this.loading = true
      const formData = new FormData()
      formData.append('file', this.importFile)
      apiServiceForm(`/api/host/events/${this.event.id}/codes/import/`, 'POST', formData).then((data) => {
        if (data && !data.err) {
          this.successMessage = `Importados ${data.created || 0} codigos nuevos.`
          this.importFile = null
          this.fetchCodes()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudieron importar los codigos.'
      }).finally(() => {
        this.loading = false
      })
    },

    toggleStatus(eventCode) {
      this.resetMessages()
      this.loading = true

      const endpoint = `/api/host/events/codes/${eventCode.id}/`
      const method = eventCode.is_active ? 'DELETE' : 'PATCH'
      const payload = eventCode.is_active ? undefined : { is_active: true, code: eventCode.code, label: eventCode.label }

      apiService(endpoint, method, payload).then((data) => {
        if (data && !data.err) {
          this.successMessage = eventCode.is_active ? 'Codigo archivado.' : 'Codigo reactivado.'
          this.fetchCodes()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo actualizar el codigo.'
      }).finally(() => {
        this.loading = false
      })
    },

    copyPromoLink(code) {
      const fullUrl = `${window.location.origin}/${this.promoUrl(code).replace(/^#\//, '#/')}`
      navigator.clipboard.writeText(fullUrl).then(() => {
        this.successMessage = `Link de ${code} copiado.`
      }).catch(() => {
        this.errorMessage = 'No se pudo copiar el link.'
      })
    },
  },
}
</script>

<style scoped>
.workspace-code-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.workspace-code-card {
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(223, 214, 255, 0.08);
}

.workspace-code-card__top {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.workspace-code-card__top h3 {
  margin: 8px 0 6px;
  color: var(--text-main);
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.8rem;
}

.workspace-code-card__top p,
.workspace-code-card__meta {
  color: var(--text-soft);
}

.workspace-code-card__eyebrow {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(90, 210, 165, 0.12);
  color: #cbffe8;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.workspace-code-card__meta {
  display: grid;
  gap: 8px;
  font-size: 0.82rem;
}

.workspace-code-card__meta span {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.workspace-code-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 720px) {
  .workspace-code-card__top {
    display: grid;
  }

  .workspace-code-card__actions {
    display: grid;
  }
}
</style>
